// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useAuthContext } from '@tls/auth';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import useShareApi from 'hooks/useShareApi';
import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { toggleShareDialog } from 'store/reducers/projectShareSlice';
import { fetchProjects, removeProject } from 'store/reducers/projectsSlice';
import { AppRoutes } from 'utilities/router/AppRoutes';
import styled from 'styled-components';
import { Button, Dialog, FooterButton, InputFieldText, Table } from '@tls/treact-ui';
import { nativeScrollViewProps } from 'components/common/scroll/TreactNativeScrollStyles';
import { IconLoading, IconPlus } from '@tls/treact-ui/icons';
export default function ShareProjectDialog() {
  const { t } = useTranslation();
  const [newUsername, setNewUsername] = React.useState('');

  const { show, id } = useAppSelector(s => s.shareProject);
  const { selectedProject } = useProjectsMetadata(id);
  const { projectName } = selectedProject ?? {};

  const dispatch = useAppDispatch();
  const onClose = useCallback(() => dispatch(toggleShareDialog()), [dispatch]);

  const { loadingUsers, addAccess, removeAccess, users } = useShareApi(id ?? '');

  const [loadingSave, setLoadingSave] = useState(false);
  const onSave = useCallback(async () => {
    setLoadingSave(true);
    const { status } = await addAccess(newUsername);

    ToastByStatus(
      status,
      t('project.settings.share.title', { name: projectName }),
      t('project.settings.share.error', { name: newUsername })
    );

    if (status === 200) {
      setNewUsername('');
    }
    setLoadingSave(false);
  }, [addAccess, newUsername, projectName, t]);

  const { currentUser } = useAuthContext();
  const isSelf = useCallback(
    (userId: string) => !!currentUser?.userName && userId.toUpperCase() === currentUser.userName.toUpperCase(),
    [currentUser?.userName]
  );

  const navigate = useNavigate();
  const [removingUserId, setRemovingUserId] = useState<string | undefined>();
  const [selfRemovalUserId, setSelfRemovalUserId] = useState<string | undefined>();

  const onRemove = useCallback(
    (userId: string) => async () => {
      // Removing yourself revokes your own access, so ask first
      if (isSelf(userId)) {
        setSelfRemovalUserId(userId);
        return;
      }

      setRemovingUserId(userId);
      const { status } = await removeAccess(userId);
      ToastByStatus(
        status,
        t('project.settings.share.title', { name: projectName }),
        t('project.settings.share.remove.error', { name: userId })
      );
      setRemovingUserId(undefined);
    },
    [isSelf, projectName, removeAccess, t]
  );

  const onCancelSelfRemoval = useCallback(() => setSelfRemovalUserId(undefined), []);
  const onConfirmSelfRemoval = useCallback(async () => {
    const userId = selfRemovalUserId ?? '';
    setRemovingUserId(userId);
    const { status } = await removeAccess(userId);
    setRemovingUserId(undefined);
    setSelfRemovalUserId(undefined);

    if (!(status >= 200 && status < 400)) {
      // 403 = last user with access, the project must not be orphaned
      ToastByStatus(
        status,
        t('project.settings.share.title', { name: projectName }),
        status === 403
          ? t('project.settings.share.remove.lastUser', { name: projectName })
          : t('project.settings.share.remove.error', { name: userId })
      );
      return;
    }

    ToastByStatus(
      status,
      t('project.settings.share.title', { name: projectName }),
      undefined,
      t('project.settings.share.remove.success', { name: userId })
    );

    dispatch(removeProject(id ?? ''));
    dispatch(fetchProjects());
    dispatch(toggleShareDialog());
    void navigate(AppRoutes.ProjectTilesFull);
  }, [dispatch, id, navigate, projectName, removeAccess, selfRemovalUserId, t]);

  const generateUserRow = useCallback(
    (user: string) => {
      return (
        <Table.Row key={user}>
          <Table.Cell width={1} alignment='LEFT'>
            {user}
          </Table.Cell>
          <Table.Cell width={'fill'}>
            {removingUserId === user ? (
              <IconLoading height='1.5rem' />
            ) : (
              <Button
                variant='secondary'
                iconKey='IconDelete'
                onClick={onRemove(user)}
                data-testid={`project-share-dialog-user-${user}-remove-button`}
              />
            )}
          </Table.Cell>
        </Table.Row>
      );
    },
    [onRemove, removingUserId]
  );

  let saveIcon = <IconPlus />;
  if (loadingSave) {
    saveIcon = <IconLoading />;
  }

  const loadingRow = useMemo(
    () => (
      <Table.Row>
        <Table.Cell width={1} alignment='CENTER'>
          <IconLoading />
          <span className='pushRight'>{t('loading')}</span>
        </Table.Cell>
      </Table.Row>
    ),
    [t]
  );

  const selfRemovalDialog = (
    <Dialog
      enableClosing
      open={!!selfRemovalUserId}
      size={'small'}
      onClose={onCancelSelfRemoval}
      data-testid='project-share-self-removal-dialog'
    >
      <Dialog.DialogHeader label={t('project.settings.share.remove.self.title')} showCloseButton />
      <Dialog.DialogContent>
        <div data-testid='project-share-self-removal-dialog-content'>
          <Trans i18nKey={'project.settings.share.remove.self.details'} values={{ name: projectName }} />
        </div>
      </Dialog.DialogContent>
      <Dialog.Footer
        right={[
          <FooterButton
            key='cancel'
            label={t('cancel')}
            onClick={onCancelSelfRemoval}
            variant='secondary'
            data-testid='project-share-self-removal-dialog-cancel-button'
          />,
          <FooterButton
            key='confirm'
            label={t('project.settings.share.remove.self.confirm')}
            icon={removingUserId ? <IconLoading /> : undefined}
            disabled={!!removingUserId}
            onClick={onConfirmSelfRemoval}
            data-testid='project-share-self-removal-dialog-confirm-button'
          />,
        ]}
      />
    </Dialog>
  );

  return (
    <>
      {selfRemovalDialog}
      <Dialog enableClosing open={show} size={'large'} onClose={onClose} data-testid='project-share-dialog'>
        <Dialog.DialogHeader label={t('project.settings.share.title', { name: projectName })} />
        <Dialog.DialogContent>
          <AddUserRegion data-testid='project-share-dialog-content'>
            <InputFieldText
              asAtom
              placeholder={t('project.settings.share.enterUsername')}
              value={newUsername}
              onValueChange={setNewUsername}
              data-testid='project-share-dialog-user-input'
            />
            <Button
              onClick={onSave}
              icon={saveIcon}
              disabled={loadingSave || newUsername?.length < 1}
              data-testid='project-share-dialog-add-user-button'
            />
          </AddUserRegion>
          <Title className='font-size_m font-weight_bold'>{t('project.settings.share.userTitle')}</Title>
          <Table list={users} headless>
            {userList => (
              <ShareTableBody scrollViewProps={nativeScrollViewProps}>
                {loadingUsers ? loadingRow : userList.map(generateUserRow)}
              </ShareTableBody>
            )}
          </Table>
        </Dialog.DialogContent>
        <Dialog.Footer
          right={[
            <FooterButton
              key='cancel'
              label={t('close')}
              onClick={onClose}
              variant='secondary'
              data-testid='project-share-dialog-close-button'
            />,
          ]}
        />
      </Dialog>
    </>
  );
}

const AddUserRegion = styled.div`
  display: flex;
  column-gap: 1rem;
  margin-bottom: 1rem;
`;

const ShareTableBody = styled(Table.ScrollingBody)`
  .pushRight {
    padding-left: 0.5rem;
  }
`;

const Title = styled.span`
  margin-bottom: 1rem;
`;
